"use client";

import { APP_URL } from "@/lib/constants";
import { useAppEvents } from "@jace/insights";
import { Icon } from "@jace/ui";
import { X } from "lucide-react";
import { type PostHog, usePostHog } from "posthog-js/react";
import { useEffect, useState } from "react";

interface PermissionsModalProps {
	open?: boolean;
	onOpenChange?: (open: boolean) => void;
}

function getAuthUrl(redirectUrl = "", posthog?: PostHog) {
	const searchParams = new URLSearchParams();
	searchParams.set("redirect_url", redirectUrl);

	if (posthog?.get_session_id) {
		const posthogSessionId = posthog.get_session_id();
		if (posthogSessionId) {
			// Add it as a separate state parameter - this gets stored in session
			searchParams.set("ph_session_id", posthogSessionId);
		}
	}

	const currentSearchParams = new URLSearchParams(window.location.search);
	if (currentSearchParams.has("test_user")) {
		const testUser = currentSearchParams.get("test_user");
		if (testUser) {
			searchParams.set("test_user", testUser);
		}
	}
	return `${process.env.NEXT_PUBLIC_API_URL}/oauth/google?${searchParams.toString()}`;
}

function getScrollbarWidth() {
	return window.innerWidth - document.documentElement.clientWidth;
}

export function PermissionsModal({
	open,
	onOpenChange,
}: PermissionsModalProps) {
	const [isOpen, setIsOpen] = useState(open ?? false);
	const { emit } = useAppEvents();
	const posthog = usePostHog();

	const closeModal = () => {
		setIsOpen(false);
		onOpenChange?.(false);
	};

	useEffect(() => {
		if (open) {
			const scrollbarWidth = getScrollbarWidth();
			const prevOverflow = document.body.style.overflow;
			const prevPaddingRight = document.body.style.paddingRight;

			document.body.style.overflow = "hidden";
			document.body.style.paddingRight = `${scrollbarWidth}px`;

			return () => {
				document.body.style.overflow = prevOverflow;
				document.body.style.paddingRight = prevPaddingRight;
			};
		}
	}, [open]);

	if (!open && !isOpen) return null;

	return (
		<div className="fixed inset-0 z-50 flex items-start md:items-center justify-center bg-black/50 p-2 overflow-y-auto h-svh w-svw">
			<div className="relative w-full md:max-w-md rounded-xl p-6 shadow-lg bg-surface-bg my-auto">
				<button
					type="button"
					onClick={closeModal}
					className="absolute right-4 top-4 text-text-muted hover:text-white"
				>
					<X className="h-5 w-5" />
					<span className="sr-only">Close</span>
				</button>

				<div className="space-y-6">
					<div className="space-y-2">
						<h2 className="text-xl font-semibold text-white">
							Connecting Your Gmail Account
						</h2>
						<p className="text-sm text-white">
							Jace needs access to your Google Calendar, Contacts, and Gmail. On
							the next screen, Google will ask for your approval.
						</p>
					</div>

					<div className="overflow-hidden rounded-lg">
						<img
							src="/images/google-permissions.png"
							alt="Google permissions screen"
							className="w-full"
						/>
					</div>

					<p className="text-sm text-white">
						<strong>We value your privacy:</strong> Jace will{" "}
						<strong>never make changes to your account or send emails</strong>{" "}
						without your explicit action within our app.
					</p>

					<div className="pt-2">
						<button
							type="button"
							onClick={() => {
								emit({
									type: "continue_with_google_click",
								});
								window.location.href = getAuthUrl(
									`${APP_URL}/getting-started`,
									posthog,
								);
							}}
							className="w-full py-3 px-4 rounded-lg cursor-pointer bg-white text-text-body-inverted font-medium flex items-center justify-center space-x-2 hover:opacity-90 transition-opacity"
						>
							<Icon kind="google" className="w-5 h-5" />
							<span>Connect Gmail account</span>
						</button>
					</div>
				</div>
			</div>
		</div>
	);
}
